import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Rnd } from "react-rnd";
import { createPortal } from "react-dom";
import {
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Tooltip,
  Typography,
  Box,
} from "@mui/material";
import { CopyAll, Close, UnfoldLess, UnfoldMore, Edit } from "@mui/icons-material";
import { getDropDownVal } from "../dropdownUtils";

const SuggestionActReferenceModal = ({ open, onClose, data }) => {
  const [type, setType] = useState("1");
  const [search, setSearch] = useState("");
  const [minimized, setMinimized] = useState(true);
  const [suggestionOptions, setSuggestionOptions] = useState([]);
  const [actrefOptions, setActrefOptions] = useState([]);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [editText, setEditText] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState("");

  const [minimizedSize, setMinimizedSize] = useState({
    width: Math.max(250, window.innerWidth * 0.2),
    height: Math.max(100, window.innerHeight * 0.15),
  });

  const [maximizedSize, setMaximizedSize] = useState({
    width: Math.min(600, window.innerWidth * 0.5),
    height: Math.min(500, window.innerHeight * 0.75),
  });

  const [rndSize, setRndSize] = useState(maximizedSize);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [localData, setLocalData] = useState([]);

  // Adjust sizes on window resize
  useEffect(() => {
    const updateSizes = () => {
      setMinimizedSize({
        width: Math.max(250, window.innerWidth * 0.2),
        height: Math.max(100, window.innerHeight * 0.15),
      });
      setMaximizedSize({
        width: Math.min(600, window.innerWidth * 0.5),
        height: Math.min(500, window.innerHeight * 0.75),
      });
    };

    window.addEventListener("resize", updateSizes);
    return () => window.removeEventListener("resize", updateSizes);
  }, []);

  useEffect(() => {
    if (open) {
      setMinimized(false); // always expanded

      const centerX = (window.innerWidth - maximizedSize.width) / 2;
      const centerY = (window.innerHeight - maximizedSize.height) / 2;

      setRndSize(maximizedSize);
      setPosition({
        x: centerX > 0 ? centerX : 0,
        y: centerY > 0 ? centerY : 0
      });
    }
  }, [open, maximizedSize]);



  useEffect(() => {
    setRndSize(minimized ? minimizedSize : maximizedSize);
  }, [minimized, minimizedSize, maximizedSize]);

  useEffect(() => {
    setLocalData(data);
  }, [data]);

  useEffect(() => {
    try {
      const sugg = getDropDownVal("SUGG") || [];
      setSuggestionOptions(sugg.map(op => ({ value: op.code, label: op.value })));

      const acts = getDropDownVal("ACTF") || [];
      setActrefOptions(acts.map(op => ({ value: op.code, label: op.value })));
    } catch (err) {
      console.warn("Error loading dropdowns");
    }
  }, []);

  const handleCopy = (text) => navigator.clipboard.writeText(text);
  const handleClose = () => onClose();
  const toggleMinimize = () => setMinimized(prev => !prev);

  const getHeaderLabel = (headerCode) => {
    if (type === "3") {
      return 'Observation';
    } else {
      const options = type === "1" ? suggestionOptions : actrefOptions;
      const found = options.find(opt => opt.value === headerCode);
      return found ? found.label : String(headerCode || '');
    }
  };

  const filtered = useMemo(() => {
    const optionsReady =
      (type === "1" && suggestionOptions.length > 0) ||
      (type === "2" && actrefOptions.length > 0) ||
      (type === "3") ||
      (type === "4");

    if (!optionsReady) return [];

    return localData
      .filter(item => item.type.toString() === type)
      .map(item => ({ ...item, headerLabel: getHeaderLabel(item.header) }))
      .filter(item =>
      (item.headerLabel?.toLowerCase().includes(search.toLowerCase()) ||
        item.name?.toLowerCase().includes(search.toLowerCase()) ||
        item.content?.toLowerCase().includes(search.toLowerCase()))
      );
  }, [localData, type, suggestionOptions, actrefOptions, search]);

  const handleEditClick = (item) => {
    setEditItem(item);
    setEditText(item.content);
    setEditModalOpen(true);
    setAiError("");
  };

  const handleEditSave = () => {
    if (editItem) {
      setLocalData(prev =>
        prev.map(d => (d.id === editItem.id ? { ...d, content: editText } : d))
      );
    }
    setEditModalOpen(false);
    setAiError("");
  };

  const handleAskAI = async () => {
    if (!editText.trim()) {
      setAiError("Please enter some content first");
      return;
    }

    setAiLoading(true);
    setAiError("");

    try {
      const encodedMsg = encodeURIComponent(editText);
      const response = await axios.get(
        `http://localhost:8080/NLP/message?msg=${encodedMsg}&count=1`
      );

      // response.data is already the string content
      const result = response.data;

      // Clean up the response by removing any leading special characters and trimming
      const cleanedResult = result.replace(/^[^\w\s]+\s*/, '').trim();

      // Set the AI response as the new edit text
      setEditText(cleanedResult);
    } catch (error) {
      console.error("AI API Error:", error);
      if (error.message === 'Network Error' || error.code === 'ERR_NETWORK') {
        setAiError("CORS Error: Unable to connect to API. Please configure CORS on the backend.");
      } else {
        setAiError("Failed to get AI response. Please try again.");
      }
    } finally {
      setAiLoading(false);
    }
  };

  if (!open) return null;

  return createPortal(
    <>
      <Rnd
        position={position}
        size={rndSize}
        bounds="window"
        onDragStop={(e, d) => setPosition({ x: d.x, y: d.y })}
        onResizeStop={(e, dir, ref, delta, newPos) => {
          setRndSize({
            width: ref.offsetWidth,
            height: ref.offsetHeight,
          });
          setPosition(newPos);
        }}
        minWidth={300}
        minHeight={200}
        enableResizing={!minimized}
        disableDragging={editModalOpen}
        dragHandleClassName="drag-header"
        style={{
          zIndex: 1300,
          borderRadius: 12,
          overflow: "hidden",
          boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
          background: "white",
        }}
      >

        <Paper
          elevation={0}
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            backgroundColor: "#fdfdfd"
          }}
        >
          {/* HEADER */}
          <Box
            className="drag-header"
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              backgroundColor: "#1976d2",
              color: "white",
              px: 2,
              py: 1,
              cursor: "move",
              userSelect: "none",
            }}
          >
            <Typography fontWeight={600}>Suggestions / Act References</Typography>
            <Box>
              <Tooltip title={minimized ? "Expand" : "Minimize"}>
                <IconButton size="small" onClick={toggleMinimize} sx={{ color: "white" }}>
                  {minimized ? <UnfoldMore fontSize="small" /> : <UnfoldLess fontSize="small" />}
                </IconButton>
              </Tooltip>
              <Tooltip title="Close">
                <IconButton size="small" onClick={handleClose} sx={{ color: "white" }}>
                  <Close fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* BODY */}
          <Box sx={{ p: 2, flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
            <ToggleButtonGroup
              value={type}
              exclusive
              onChange={(e, val) => val && setType(val)}
              size="small"
              sx={{
                alignSelf: "center",
                mb: 1,
                "& .MuiToggleButton-root": { px: 2, fontWeight: 500 }
              }}
            >
              <ToggleButton value="1">Suggestion</ToggleButton>
              <ToggleButton value="2">Act Reference</ToggleButton>
              <ToggleButton value="3">Observations</ToggleButton>
              <ToggleButton value="4">Acknowledgement</ToggleButton>
            </ToggleButtonGroup>

            {!minimized && (
              <>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Search by Header..."
                  size="small"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  sx={{
                    backgroundColor: "white",
                    borderRadius: 1
                  }}
                />

                {/* TABLE */}
                <TableContainer
                  sx={{
                    flex: 1,
                    borderRadius: 2,
                    border: "1px solid #ddd",
                    backgroundColor: "white",
                    overflow: "auto",
                    maxHeight: `calc(${rndSize.height}px - 180px)`
                  }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: "bold" }}>Header</TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>Content</TableCell>
                        <TableCell sx={{ fontWeight: "bold" }} align="center">
                          Action
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filtered.length > 0 ? (
                        filtered.map((item) => (
                          <TableRow key={item.id} hover>
                            <TableCell>{item.headerLabel}</TableCell>
                            <TableCell>{item.name}</TableCell>
                            <TableCell sx={{ whiteSpace: "pre-wrap" }}>{item.content}</TableCell>
                            <TableCell align="center">
                              <Tooltip title="Copy">
                                <IconButton
                                  size="small"
                                  onClick={() => handleCopy(item.content)}
                                  sx={{ color: "#1976d2" }}
                                >
                                  <CopyAll fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Edit">
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditClick(item)}
                                  sx={{ color: "#1976d2" }}
                                >
                                  <Edit fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={4} align="center" sx={{ color: "#777" }}>
                            {(type === "1" && suggestionOptions.length === 0) ||
                              (type === "2" && actrefOptions.length === 0)
                              ? "Loading options..."
                              : "No records found."}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </Box>
        </Paper>
      </Rnd>
      {editModalOpen && (
        <Box
          sx={{
            position: "fixed",
            top: 50,
            left: 290,
            width: "80%",
            maxWidth: 500,
            maxHeight: "80vh",
            backgroundColor: "white",
            border: "2px solid #1976d2",
            borderRadius: 2,
            boxShadow: 5,
            zIndex: 2000,
            p: 2,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography variant="h6" gutterBottom>
            Edit Content
          </Typography>

          {/* SCROLLABLE AREA */}
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              pr: 1,
            }}
          >
            <TextField
              multiline
              fullWidth
              minRows={6}
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              variant="outlined"
              autoFocus
              disabled={aiLoading}
            />

            {aiError && (
              <Typography
                color="error"
                variant="caption"
                sx={{ mt: 1, display: "block" }}
              >
                {aiError}
              </Typography>
            )}
          </Box>

          {/* FOOTER (always visible) */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mt: 2,
              gap: 1,
            }}
          >
            <Button
              onClick={() => {
                setEditModalOpen(false);
                setAiError("");
              }}
              disabled={aiLoading}
            >
              Cancel
            </Button>

            <Button
              onClick={handleAskAI}
              variant="outlined"
              disabled={aiLoading}
              sx={{
                borderColor: "#9c27b0",
                color: "#9c27b0",
                "&:hover": {
                  borderColor: "#7b1fa2",
                  backgroundColor: "#f3e5f5",
                },
              }}
            >
              {aiLoading ? "Processing..." : "Ask AI"}
            </Button>

            <Button
              onClick={handleEditSave}
              variant="contained"
              disabled={aiLoading}
            >
              Save
            </Button>
          </Box>
        </Box>
      )}
    </>,
    document.body
  );
};

export default SuggestionActReferenceModal;